import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import AdminLayout from "@/components/AdminLayout";
import { Plus, Edit, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MultiImageUploader from "@/components/MultiImageUploader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const AdminUniverses = () => {
  const [universes, setUniverses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUniverse, setEditingUniverse] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    formed_date: "",
    time_period: "",
    cover_image: "",
    images: [],
    category: "sci-fi",
  });

  const fetchUniverses = async () => {
    try {
      const response = await axios.get(`${API}/universes`);
      setUniverses(response.data);
    } catch (error) {
      toast.error("Failed to fetch universes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUniverses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUniverse) {
        await axios.put(`${API}/universes/${editingUniverse.slug}`, formData);
        toast.success("Universe updated");
      } else {
        await axios.post(`${API}/universes`, formData);
        toast.success("Universe created");
      }
      setDialogOpen(false);
      resetForm();
      fetchUniverses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm("Are you sure you want to delete this universe?")) return;
    try {
      await axios.delete(`${API}/universes/${slug}`);
      toast.success("Universe deleted");
      fetchUniverses();
    } catch (error) {
      toast.error("Failed to delete universe");
    }
  };

  const openEditDialog = (universe) => {
    setEditingUniverse(universe);
    setFormData({
      name: universe.name,
      slug: universe.slug,
      description: universe.description,
      formed_date: universe.formed_date || "",
      time_period: universe.time_period || "",
      cover_image: universe.cover_image || "",
      images: universe.images || [],
      category: universe.category,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUniverse(null);
    setFormData({
      name: "",
      slug: "",
      description: "",
      formed_date: "",
      time_period: "",
      cover_image: "",
      images: [],
      category: "sci-fi",
    });
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  return (
    <AdminLayout title="Manage Universes">
      <div className="flex items-center justify-between mb-6">
        <p className="text-slate-400">{universes.length} universes</p>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-universe-btn"
        >
          <Plus className="w-4 h-4" />
          Add Universe
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Formed</th>
                <th>Time Period</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {universes.map((universe) => (
                <tr key={universe.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <img
                        src={universe.cover_image || "https://static.prod-images.emergentagent.com/jobs/f60715c2-3b6b-4c99-a6c8-5c96b98e6cf9/images/53108f7d8ec9c28606e03e3c421e51fe4b80f13705cd9eeafc38c88182bd88ad.png"}
                        alt={universe.name}
                        className="w-10 h-10 object-cover"
                      />
                      <div>
                        <div className="font-medium text-white">{universe.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{universe.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td><span className={`badge-${universe.category} px-2 py-1 text-xs uppercase`}>{universe.category}</span></td>
                  <td className="text-slate-400">{universe.formed_date}</td>
                  <td className="text-slate-400">{universe.time_period}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(universe)}
                        data-testid={`edit-universe-${universe.slug}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(universe.slug)}
                        className="hover:text-red-500"
                        data-testid={`delete-universe-${universe.slug}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0B0F19] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-['Outfit']">
              {editingUniverse ? "Edit Universe" : "Add New Universe"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="universe-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      name: e.target.value,
                      slug: editingUniverse ? formData.slug : generateSlug(e.target.value)
                    });
                  }}
                  className="input-dark mt-1"
                  required
                  data-testid="universe-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Slug</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="input-dark mt-1"
                  required
                  disabled={!!editingUniverse}
                  data-testid="universe-slug"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input-dark mt-1 min-h-[100px]"
                required
                data-testid="universe-description"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="input-dark mt-1" data-testid="universe-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111827] border-white/10">
                    <SelectItem value="sci-fi">Sci-Fi</SelectItem>
                    <SelectItem value="fantasy">Fantasy</SelectItem>
                    <SelectItem value="superhero">Superhero</SelectItem>
                    <SelectItem value="horror">Horror</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Formed Date</Label>
                <Input
                  value={formData.formed_date}
                  onChange={(e) => setFormData({ ...formData, formed_date: e.target.value })}
                  className="input-dark mt-1"
                  placeholder="e.g., 1977"
                  data-testid="universe-formed"
                />
              </div>
              <div>
                <Label className="text-slate-300">Time Period</Label>
                <Input
                  value={formData.time_period}
                  onChange={(e) => setFormData({ ...formData, time_period: e.target.value })}
                  className="input-dark mt-1"
                  placeholder="e.g., 24th Century"
                  data-testid="universe-period"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Cover Image URL (or upload below)</Label>
              <Input
                value={formData.cover_image}
                onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                className="input-dark mt-1"
                placeholder="https://..."
                data-testid="universe-image"
              />
            </div>

            <div>
              <Label className="text-slate-300">Gallery Images</Label>
              <div className="mt-1">
                <MultiImageUploader
                  images={formData.images}
                  onChange={(imgs) => setFormData({ ...formData, images: imgs, cover_image: imgs[0] || formData.cover_image })}
                  maxImages={10}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="universe-submit">
                {editingUniverse ? "Update" : "Create"} Universe
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminUniverses;
